import { capitalize, mapKeys, mapValues, orderBy, pick } from 'lodash';

import AnchorWrapper from '@/components/AnchorWrapper';
import VMPGenericChip from '@/components/VMPGenericChip';
import { CRITICALITY_LABELS } from '@/constants/criticalitylabels.constants';
import type { Asset } from '@/pages/Assets/Assets.types';
import { KEY_MAPS } from '@/pages/VulnerabilityManagement/VulnerabilityManagement.constants';
import type {
  RemediationType,
  VulnerabilityOrRemediationType,
  VulnerabilityType
} from '@/pages/VulnerabilityManagement/VulnerabilityManagement.types';
import getPonduranceScoreSeverity from '@/utils/getPonduranceScoreSeverity';

export const addVMPECriticalities = (
  vulnerabilities: (
    | RemediationType
    | VulnerabilityType
    | VulnerabilityOrRemediationType
  )[]
) => {
  const vulnerabilitiesWithScores = vulnerabilities.map(
    (
      vuln: RemediationType | VulnerabilityType | VulnerabilityOrRemediationType
    ) => {
      const numberValue = vuln?.shield_exposure_index || 0;
      const vulnWithScore = vuln;
      const ponduranceScore = getPonduranceScoreSeverity(numberValue);
      vulnWithScore.pondurance_score = ponduranceScore;
      return vulnWithScore;
    }
  );

  return vulnerabilitiesWithScores;
};

export const replaceUnwantedValues = <T extends object>(
  items: T[] | null | undefined
): T[] => {
  if (!items) {
    return [];
  }

  const updatedItems = items.map((item) =>
    mapValues(item, (itemValue) => {
      if (itemValue === null || itemValue === 'None') {
        return '';
      }
      if (Array.isArray(itemValue)) {
        // Recursively apply to arrays of objects
        return Array.isArray(itemValue[0])
          ? itemValue.map((v) => replaceUnwantedValues(v))
          : replaceUnwantedValues(itemValue as object[]);
      }
      return itemValue;
    })
  ) as T[];

  return updatedItems;
};

export const exposureIndexFormatter = (
  numberValue: number,
  severity: string,
  name: string | number
) => {
  return (
    <AnchorWrapper
      path="/vulnerability-management/vulnerability-details"
      itemId={name}
    >
      <VMPGenericChip
        type="outlined"
        value={severity}
        text={`${numberValue} ${severity}`}
      />
    </AnchorWrapper>
  );
};

export const getCvssLevel = (severityNumber: number) => {
  switch (true) {
    case severityNumber < 4:
      return CRITICALITY_LABELS.LOW;
    case severityNumber < 7:
      return CRITICALITY_LABELS.MEDIUM;
    case severityNumber < 9:
      return CRITICALITY_LABELS.HIGH;
    case severityNumber >= 9:
      return CRITICALITY_LABELS.CRITICAL;
    default:
      return CRITICALITY_LABELS.LOW;
  }
};

export const mapCvssLevel = (
  vulnerabilities: RemediationType[] | VulnerabilityType[]
) => {
  return vulnerabilities.map((vuln) => {
    const cvssNumber = vuln.cvss || 0;
    const cvssLevel = getCvssLevel(cvssNumber);
    return {
      ...vuln,
      cvss_level: cvssLevel
    };
  });
};

export const severityFormatter = (
  name: string | number,
  severityNumber: number,
  severityLevel: string
) => {
  return (
    <AnchorWrapper
      path="/vulnerability-management/vulnerability-details"
      itemId={name}
    >
      <VMPGenericChip
        value={String(severityLevel).toUpperCase()}
        text={`${severityNumber} ${capitalize(severityLevel)}`}
      />
    </AnchorWrapper>
  );
};

export const cellFormatter = (
  name?: string | null | number,
  text?: string | number | null
) => (
  <AnchorWrapper
    path="/vulnerability-management/vulnerability-details"
    itemId={name}
  >
    {text || ''}
  </AnchorWrapper>
);

export const sortExportColumnsData = (
  rows: Asset[] | RemediationType[] | VulnerabilityType[],
  columns: string[],
  defaultSortKey: string,
  orderByValue: string,
  orderValue: 'asc' | 'desc'
) => {
  // replace keys based on KEY_MAPS, otherwise keep as-is
  const newRows = rows.map((row) =>
    mapKeys(row, (_value, key) =>
      key in KEY_MAPS ? KEY_MAPS[key as keyof typeof KEY_MAPS] : key
    )
  );

  // pick only the columns that are in the columns array
  newRows.map((row) => pick(row, columns));

  // sort the rows based on the orderByValue and orderValue
  const sortedNewRows = orderBy(
    newRows,
    orderByValue !== 'id' ? orderByValue : defaultSortKey,
    orderByValue !== 'id' && orderValue === 'desc' ? 'desc' : 'asc'
  );

  return sortedNewRows;
};
